/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface SliderProps {
  initialValue: number;
  minValue: number;
  maxValue: number;
  step: number;
  onValueChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  showLabel?: boolean;
  label?: string;
  labelClassName?: string;
  showTooltip?: boolean;
}

export function Slider({
  initialValue,
  minValue,
  maxValue,
  step,
  onValueChange,
  disabled = false,
  className = '',
  showLabel = true,
  label = 'Speed',
  labelClassName = '',
  showTooltip = false,
}: SliderProps) {
  const [value, setValue] = useState(initialValue);
  const [isDragging, setIsDragging] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(showTooltip);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Convert value to percentage for positioning
  const valueToPercentage = useCallback(
    (currentValue: number) => {
      return ((currentValue - minValue) / (maxValue - minValue)) * 100;
    },
    [minValue, maxValue]
  );

  // Convert percentage to value
  const percentageToValue = useCallback(
    (percentage: number) => {
      const rawValue = (percentage / 100) * (maxValue - minValue) + minValue;
      return Math.round(rawValue / step) * step;
    },
    [minValue, maxValue, step]
  );

  const handleValueChange = useCallback(
    (newValue: number) => {
      const clampedValue = Math.max(minValue, Math.min(maxValue, newValue));
      setValue(clampedValue);
      onValueChange?.(clampedValue);
    },
    [minValue, maxValue, onValueChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);
      setIsTooltipVisible(true);
    },
    [disabled]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(
        0,
        Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
      );
      const newValue = percentageToValue(percentage);
      handleValueChange(newValue);
    },
    [isDragging, percentageToValue, handleValueChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsTooltipVisible(showTooltip);
  }, [showTooltip]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || isDragging) return;

      const rect = sliderRef.current!.getBoundingClientRect();
      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
      const newValue = percentageToValue(percentage);
      handleValueChange(newValue);
    },
    [disabled, isDragging, percentageToValue, handleValueChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      let newValue = value;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          newValue = Math.max(minValue, value - step);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          newValue = Math.min(maxValue, value + step);
          break;
        case 'Home':
          newValue = minValue;
          break;
        case 'End':
          newValue = maxValue;
          break;
        default:
          return;
      }

      e.preventDefault();
      handleValueChange(newValue);
    },
    [disabled, value, minValue, maxValue, step, handleValueChange]
  );

  // Add global mouse events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const percentage = valueToPercentage(value);

  // Value indicator color based on value
  const getValueColor = () => {
    if (value < 1) return 'text-status-orange dark:text-status-orange';
    if (value > 1) return 'text-status-green dark:text-status-green';
    return 'text-primary-500 dark:text-primary-500';
  };

  const getTrackColor = () => {
    if (value < 1) return 'bg-status-orange';
    if (value > 1) return 'bg-status-green';
    return 'bg-primary-500';
  };

  const getBorderColor = () => {
    if (value < 1) return 'border-status-orange';
    if (value > 1) return 'border-status-green';
    return 'border-primary-500';
  };

  return (
    <div className={`inline-flex items-center space-x-2 ${className}`}>
      {/* Label */}
      {showLabel && (
        <div className={`flex items-center space-x-1 ${labelClassName}`}>
          <svg
            className="text-onlight-text-2 dark:text-ondark-text-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <span className="text-onlight-text-2 dark:text-ondark-text-2 whitespace-nowrap text-xs">
            {label}
          </span>
        </div>
      )}

      {/* Slider Track */}
      <div className="group relative">
        <div
          ref={sliderRef}
          className={`bg-onlight-bg-3 dark:bg-ondark-bg-3 h-2 w-28 cursor-pointer rounded-full transition-all duration-150 ${
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'group-hover:bg-onlight-line-2 dark:group-hover:bg-ondark-line-2 group-hover:h-2'
          }`}
          onClick={handleClick}
          role="slider"
          aria-valuemin={minValue}
          aria-valuemax={maxValue}
          aria-valuenow={value}
          aria-label={`${label} slider`}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
        >
          {/* Track fill */}
          <div
            className={`absolute left-0 top-0 h-full rounded-full transition-all duration-150 ${getTrackColor()}`}
            style={{ width: `${percentage}%` }}
          />

          {/* Thumb */}
          <div
            className={`dark:bg-ondark-bg-1 absolute top-1/2 h-3 w-3 -translate-y-1/2 cursor-grab rounded-full border-2 bg-white shadow-sm transition-all duration-150 ${getBorderColor()} ${
              isDragging ? 'scale-125 cursor-grabbing' : 'group-hover:scale-110'
            } ${disabled ? 'cursor-not-allowed' : ''}`}
            style={{ left: `calc(${percentage}% - 6px)` }}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => !disabled && setIsTooltipVisible(true)}
            onMouseLeave={() => !isDragging && setIsTooltipVisible(showTooltip)}
          />

          {/* Tooltip */}
          {isTooltipVisible && (
            <div className="bg-ondark-bg-1 dark:bg-onlight-bg-1 text-ondark-text-1 dark:text-onlight-text-1 pointer-events-none absolute -top-8 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded px-2 py-1 text-xs font-medium">
              {value}x
              <div className="border-t-ondark-bg-1 dark:border-t-onlight-bg-1 absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-2 border-r-2 border-t-2 border-transparent" />
            </div>
          )}
        </div>
      </div>

      {/* Value Display */}
      <span
        className={`min-w-[2rem] text-center text-xs font-semibold ${getValueColor()}`}
      >
        {value}x
      </span>
    </div>
  );
}

export default Slider;
